import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StateContext = createContext();

export const useStateContext = () => {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useStateContext must be used within a StateProvider');
  }
  return context;
};

export const StateProvider = ({ children }) => {
  const [currentState, setCurrentState] = useState(() => {
    // Load from localStorage or default to WA
    return localStorage.getItem('selected_state') || 'WA';
  });
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateInfo, setStateInfo] = useState(null);

  // Fetch available states on mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${API}/states`);
        if (response.data.success) {
          setStates(response.data.states);
        }
      } catch (error) {
        console.error('Failed to fetch states:', error);
        // Fallback to default states
        setStates([
          { state_code: 'WA', state_name: 'Washington', enabled: true },
          { state_code: 'CO', state_name: 'Colorado', enabled: true },
          { state_code: 'FL', state_name: 'Florida', enabled: true },
          { state_code: 'SC', state_name: 'South Carolina', enabled: true },
          { state_code: 'PA', state_name: 'Pennsylvania', enabled: true }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchStates();
  }, []);

  // Fetch state info when current state changes
  useEffect(() => {
    const fetchStateInfo = async () => {
      try {
        const response = await axios.get(`${API}/states/${currentState}`);
        if (response.data.success) {
          setStateInfo(response.data.state);
        }
      } catch (error) {
        console.error('Failed to fetch state info:', error);
      }
    };

    if (currentState) {
      fetchStateInfo();
    }
  }, [currentState]);

  const selectState = (stateCode) => {
    setCurrentState(stateCode);
    localStorage.setItem('selected_state', stateCode);
  };

  const getCurrentStateName = () => {
    const state = states.find(s => s.state_code === currentState);
    return state?.state_name || 'Washington';
  };

  const value = {
    currentState,
    states,
    stateInfo,
    loading,
    selectState,
    getCurrentStateName,
    // Helper to build state-specific API URLs
    getStateApiUrl: (endpoint) => `${API}/states/${currentState}${endpoint}`,
    // Legacy API URL (for backward compatibility with WA)
    getApiUrl: (endpoint) => currentState === 'WA' ? `${API}${endpoint}` : `${API}/states/${currentState}${endpoint}`
  };

  return (
    <StateContext.Provider value={value}>
      {children}
    </StateContext.Provider>
  );
};

export default StateContext;
